// Frontend flag for the tenant-level A2P bypass surface.
//
// A bypassed tenant (e.g. an internal demo / tester account) sends SMS under
// the PARENT CIVIX A2P Brand + Messaging Service, so there is no per-tenant
// registration to perform. When `features.a2p.hideUI` is true we hide the A2P
// registration nav entry and render a "registration handled by CIVIX" banner
// instead of the wizard.
//
// Mirrors aiDialerFlag.js — reads the same authed /api/tenant/features payload,
// which now includes `features.a2p.{hideUI,bypassEnabled}` and `a2pBypass`.

import { useEffect, useState } from 'react';
import { apiGet } from './api.js';

// Per-session cache of the runtime lookup.
let _state = null; // { hideUI, bypassEnabled }
let _inflight = null;

export async function fetchA2pBypassFlags() {
  if (_state !== null) return _state;
  if (_inflight) return _inflight;
  _inflight = apiGet('/tenant/features')
    .then((r) => {
      _state = {
        hideUI: !!r?.features?.a2p?.hideUI,
        bypassEnabled: !!(r?.features?.a2p?.bypassEnabled || r?.a2pBypass),
      };
      return _state;
    })
    .catch(() => {
      _state = { hideUI: false, bypassEnabled: false };
      return _state;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

export function clearA2pBypassCache() { _state = null; }

// React hook — { hideUI, bypassEnabled, loading }. Shares the cache across
// components and triggers the fetch on first use.
export function useA2pBypass() {
  const [state, setState] = useState(_state || { hideUI: false, bypassEnabled: false });
  const [loading, setLoading] = useState(_state === null);
  useEffect(() => {
    if (_state !== null) { setState(_state); setLoading(false); return; }
    let alive = true;
    fetchA2pBypassFlags().then((s) => {
      if (!alive) return;
      setState(s);
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);
  return { ...state, loading };
}
